import { Fragment } from "react";
import {
  Menu,
  MenuButton,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  PlayCircleIcon,
  PauseCircleIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";
import { PolicyState } from "shared";

import { classNames } from "../../../../../common/lib/classNames";

interface PolicyStateOption {
  value: PolicyState;
  label: string;
  description: string;
  icon: typeof PlayCircleIcon;
  color: `green` | `orange` | `red`;
  disabled?: boolean;
}

interface PolicyStateDropdownProps {
  value: PolicyState;
  onChange: (e: React.MouseEvent<HTMLButtonElement>, newState: PolicyState) => void;
  loading?: boolean;
  disabled?: boolean;
}

const stateConfig: Record<PolicyState, {
  label: string;
  icon: typeof PlayCircleIcon;
  color: `green` | `orange` | `red`;
  bgColor: string;
  textColor: string;
  hoverBgColor: string;
  borderColor: string;
}> = {
  [PolicyState.ACTIVE]: {
    label: `Active`,
    icon: PlayCircleIcon,
    color: `green`,
    bgColor: `bg-green-50`,
    textColor: `text-green-700`,
    hoverBgColor: `hover:bg-green-100`,
    borderColor: `border-green-300`,
  },
  [PolicyState.APPROVAL]: {
    label: `Approval`,
    icon: PauseCircleIcon,
    color: `orange`,
    bgColor: `bg-orange-50`,
    textColor: `text-orange-700`,
    hoverBgColor: `hover:bg-orange-100`,
    borderColor: `border-orange-300`,
  },
  [PolicyState.INACTIVE]: {
    label: `Inactive`,
    icon: StopCircleIcon,
    color: `red`,
    bgColor: `bg-red-50`,
    textColor: `text-red-700`,
    hoverBgColor: `hover:bg-red-100`,
    borderColor: `border-red-300`,
  },
};

export function PolicyStateDropdown({
  value,
  onChange,
  loading = false,
  disabled = false,
}: PolicyStateDropdownProps) {
  const currentState = stateConfig[value];
  const Icon = currentState.icon;

  const options: PolicyStateOption[] = [
    {
      value: PolicyState.ACTIVE,
      label: `Active`,
      description: `This policy will run automatically`,
      icon: PlayCircleIcon,
      color: `green`,
      disabled: value === PolicyState.ACTIVE,
    },
    {
      value: PolicyState.APPROVAL,
      label: `Approval`,
      description: `This policy will draft actions that require approval to run`,
      icon: PauseCircleIcon,
      color: `orange`,
      disabled: value === PolicyState.APPROVAL,
    },
    {
      value: PolicyState.INACTIVE,
      label: `Inactive`,
      description: `This policy will not run`,
      icon: StopCircleIcon,
      color: `red`,
      disabled: value === PolicyState.INACTIVE,
    },
  ];

  return (
    <Menu as={ `div` }
      className={ `relative inline-block text-left` }>
      <MenuButton
        disabled={ disabled || loading }
        className={ classNames(
          `inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm transition-colors w-[110px]`,
          currentState.bgColor,
          currentState.textColor,
          currentState.borderColor,
          currentState.hoverBgColor,
          (disabled || loading) ? `opacity-50 cursor-not-allowed` : `cursor-pointer`,
        ) }
        onClick={ e => e.stopPropagation() }
      >
        <Icon className={ `h-4 w-4` } />
        { currentState.label }
        <ChevronDownIcon className={ `h-4 w-4 ml-1` } />
      </MenuButton>

      <Transition
        as={ Fragment }
        enter={ `transition ease-out duration-100` }
        enterFrom={ `transform opacity-0 scale-95` }
        enterTo={ `transform opacity-100 scale-100` }
        leave={ `transition ease-in duration-75` }
        leaveFrom={ `transform opacity-100 scale-100` }
        leaveTo={ `transform opacity-0 scale-95` }
      >
        <Menu.Items className={ `absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden` }>
          <div className={ `py-1` }>
            { options.map(option => {
              const OptionIcon = option.icon;

              return (
                <Menu.Item key={ option.value }
                  disabled={ option.disabled }>
                  { ({ active }) => (
                    <button
                      onClick={ e => {
                        e.stopPropagation();
                        if (!option.disabled) {
                          onChange(e, option.value);
                        }
                      } }
                      disabled={ option.disabled }
                      className={ classNames(
                        active ? `bg-gray-100` : ``,
                        option.disabled ? `opacity-50 cursor-not-allowed` : ``,
                        `flex items-start gap-3 w-full px-4 py-3 text-left`,
                      ) }
                    >
                      <OptionIcon
                        className={ classNames(
                          `h-5 w-5 mt-0.5 shrink-0`,
                          option.color === `green` && `text-green-600`,
                          option.color === `orange` && `text-orange-600`,
                          option.color === `red` && `text-red-600`,
                        ) }
                      />
                      <div className={ `flex-1 min-w-0` }>
                        <div className={ `text-sm font-medium text-gray-900` }>
                          { option.label }
                        </div>
                        <div className={ `text-xs text-gray-500 mt-0.5` }>
                          { option.description }
                        </div>
                      </div>
                    </button>
                  ) }
                </Menu.Item>
              );
            }) }
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
